import { useState } from "react";
import PeptideExplainer from "@/components/ui/peptide-explainer";
import KeyTakeaway from "@/components/ui/key-takeaway";
import { PeptideStructure } from "@/assets/diagrams/peptide-structure";
import { CellSignaling } from "@/assets/diagrams/cell-signaling";

export default function WhatArePeptides() {
  return (
    <section id="what-are-peptides" className="mb-16 scroll-mt-20">
      <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
        Biological Basis of Peptides
      </h2>
      
      <div className="flex flex-col md:flex-row gap-8 mb-12">
        <div className="md:w-1/2">
          <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-4">
            What Are Peptides?
          </h3>
          <p className="mb-4">
            Peptides are short chains of amino acids—generally 2 to 50 units long—that serve 
            as the building blocks of proteins. They occur naturally throughout the body as 
            hormones, neurotransmitters, growth factors, and signaling molecules.
          </p>
          
          <PeptideExplainer 
            title="Understanding Peptide Structure"
            content={
              <>
                <p className="mb-4">Peptides consist of amino acids joined by peptide bonds. Their structure determines their function:</p>
                <ul className="list-disc pl-5 mb-4">
                  <li>Primary structure: The sequence of amino acids</li>
                  <li>Secondary structure: Local folding patterns (helices, sheets)</li>
                  <li>Tertiary structure: Overall three-dimensional shape</li>
                </ul>
                <p>This structure allows peptides to bind with specific receptors on cell surfaces, initiating biochemical reactions inside the cell.</p>
              </>
            }
          />
          
          <KeyTakeaway>
            Peptides are essentially small proteins – usually defined as chains of 50 or fewer amino acids.
            They play diverse roles in biology as hormones, neurotransmitters, and signaling molecules.
          </KeyTakeaway>
          
          <p className="mb-4">
            Think of peptides as the "words" in the language of cellular communication. 
            Each peptide delivers specific instructions to cells, triggering cascades of 
            intracellular events. This communication network keeps our bodies functioning 
            properly—regulating everything from metabolism and immune response to tissue 
            repair and hormone production.
          </p>
          
          <p>
            Unlike many pharmaceuticals that work by blocking biological processes, peptides 
            typically enhance or modulate natural functions. This biomimetic approach is a key 
            reason why peptides often produce fewer side effects than conventional drugs.
          </p>
        </div>
        
        <div className="md:w-1/2">
          <div className="rounded-lg overflow-hidden shadow-lg mb-6">
            <div className="h-64 bg-[#EBF5FB] flex items-center justify-center p-4">
              <PeptideStructure />
            </div>
            <div className="p-4 bg-white">
              <p className="text-sm text-[#7F8C8D]">
                Visualization of a peptide's molecular structure, showing the chain of amino acids linked by peptide bonds.
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6">
              <h4 className="font-bold text-[#2C3E50] mb-3">Key Characteristics of Peptides</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full border border-[#3498DB]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Precision:</strong> High specificity for their cellular targets</span>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full border border-[#3498DB]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Low Toxicity:</strong> Metabolize into amino acids with minimal waste</span>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full border border-[#3498DB]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Regulatory:</strong> Modulate rather than block biological processes</span>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full border border-[#3498DB]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Diversity:</strong> Can be engineered for various therapeutic applications</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Interactive Cell Signaling Diagram */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
        <div className="p-6">
          <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-4">
            Cellular "Language" of Signaling
          </h3>
          <p className="mb-6">
            Peptides function as messengers in the body's complex communication network. 
            When a peptide binds to a receptor on a cell's surface, it triggers a series 
            of biochemical reactions that alter the cell's behavior.
          </p>
          
          <div className="relative aspect-video bg-[#EBF5FB] rounded-lg flex justify-center items-center mb-6">
            <CellSignaling />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="bg-[#EBF5FB] p-4 rounded-lg">
              <h4 className="font-bold text-[#2C3E50] mb-2">1. Binding</h4>
              <p className="text-sm">Peptide attaches to specific receptor on cell surface</p>
            </div>
            <div className="bg-[#EBF5FB] p-4 rounded-lg">
              <h4 className="font-bold text-[#2C3E50] mb-2">2. Signaling</h4>
              <p className="text-sm">Receptor changes shape, triggering internal cell messengers</p>
            </div>
            <div className="bg-[#EBF5FB] p-4 rounded-lg">
              <h4 className="font-bold text-[#2C3E50] mb-2">3. Response</h4>
              <p className="text-sm">Cell alters its behavior based on the peptide's "message"</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Peptides "speak the language" of cell signaling by mimicking the body's own regulatory peptides. 
            This allows them to modulate biological pathways in a finely tuned way, turning processes up or down 
            rather than crudely shutting them down.
          </KeyTakeaway>
        </div>
      </div>
      
      <div className="bg-[#ECF0F1] p-6 rounded-lg">
        <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">Why This Matters</h3>
        <p className="mb-4">
          By understanding peptides as cellular messengers, scientists can develop targeted 
          treatments that work harmoniously with the body's natural systems. This represents 
          a fundamental shift from traditional pharmaceuticals that often disrupt normal 
          biological processes.
        </p>
        <p className="mb-4">
          One caveat is that natural peptides are often quickly degraded in the body, a challenge 
          for therapeutic development. Modern peptide engineering techniques focus on 
          extending half-life while preserving biological activity, creating more stable and 
          effective therapeutic peptides.
        </p>
        
        <KeyTakeaway>
          Because peptides are modeled after natural signals, they act with high specificity and fit their 
          targets like a key in a lock. This precision, combined with their natural degradation into amino acids,
          often results in fewer side effects compared to conventional drugs.
        </KeyTakeaway>
      </div>
    </section>
  );
}
